import { zeroAddress } from "viem";

import { getContract, getV1VaultAddress } from "config/contracts";
import { getWhitelistedV1Tokens } from "config/tokens";
import { useMulticall } from "lib/multicall/useMulticall";
import { FREQUENT_MULTICALL_REFRESH_INTERVAL } from "lib/timeConstants";

import MlpManager from "sdk/abis/MlpManager.json";
import ReaderV2 from "sdk/abis/ReaderV2.json";
import VaultV2 from "sdk/abis/VaultV2.json";

function buildDashboardRequest(chainId: number) {
  const moneyAddress = getContract(chainId, "MONEY");
  const mlpAddress = getContract(chainId, "MLP");
  const usdgAddress = getContract(chainId, "USDG");

  const tokensForSupplyQuery = [moneyAddress, mlpAddress, usdgAddress];

  const whitelistedTokensAddresses = getWhitelistedV1Tokens(chainId).map((token) => token.address);

  return {
    mlp: {
      contractAddress: getContract(chainId, "MlpManager"),
      abi: MlpManager.abi,
      calls: {
        getAums: {
          methodName: "getAums",
          params: [],
        },
      },
    },
    reader: {
      contractAddress: getContract(chainId, "Reader"),
      abi: ReaderV2.abi,
      calls: {
        getTokenBalancesWithSupplies: {
          methodName: "getTokenBalancesWithSupplies",
          params: [zeroAddress, tokensForSupplyQuery],
        },
        getFees: {
          methodName: "getFees",
          params: [getV1VaultAddress(chainId), whitelistedTokensAddresses],
        },
      },
    },
    vault: {
      contractAddress: getV1VaultAddress(chainId),
      abi: VaultV2.abi,
      calls: {
        totalTokenWeights: {
          methodName: "totalTokenWeights",
          params: [],
        },
      },
    },
  };
}

function parseDashboardResponse(result) {
  const minAum = result.data.mlp.getAums.returnValues[0];
  const maxAum = result.data.mlp.getAums.returnValues[1];
  const aum = (minAum + maxAum) / 2n;

  return {
    mlp: {
      aum,
    },
    reader: {
      tokenBalancesWithSupplies: {
        moneySypply: result.data.reader.getTokenBalancesWithSupplies.returnValues[1] as bigint,
        mlpSupply: result.data.reader.getTokenBalancesWithSupplies.returnValues[3] as bigint,
        usdgSupply: result.data.reader.getTokenBalancesWithSupplies.returnValues[5] as bigint,
      },
      fees: result.data.reader.getFees.returnValues as bigint[],
    },
    vault: {
      totalTokenWeights: result.data.vault.totalTokenWeights.returnValues[0] as bigint,
    },
  };
}

export type ChainStats = ReturnType<typeof parseDashboardResponse>;

export function useDashboardChainStatsMulticall(chainId: number) {
  const { data } = useMulticall(chainId, `useDashboardChainStatsMulticall`, {
    key: [chainId],
    refreshInterval: FREQUENT_MULTICALL_REFRESH_INTERVAL,
    request: buildDashboardRequest,
    parseResponse: parseDashboardResponse,
  });

  return data;
}
